import React, { useMemo } from 'react';
import { SessionStats } from '@/types';

interface CourseDayStatsProps {
  currentDay: number;
  currentRound: number;
  courseProgress: any;
  customStats?: SessionStats; // NEW: For custom mode
  mode?: 'course' | 'custom'; // NEW: To determine which stats to show
  className?: string;
}

/**
 * Aggregates session stats from multiple rounds with proper weighted averages
 */
const aggregateRoundStats = (completedRounds: SessionStats[]): SessionStats => {
  if (completedRounds.length === 0) {
    return {
      totalLetters: 0,
      correctLetters: 0,
      incorrectLetters: 0,
      totalAttempts: 0,
      wrongAttempts: 0,
      accuracy: 0,
      speed: 0,
      duration: 0,
      wpm: 0
    };
  }

  // Sum up totals
  const totalAttempts = completedRounds.reduce((sum, round) => sum + round.totalAttempts, 0);
  const correctLetters = completedRounds.reduce((sum, round) => sum + round.correctLetters, 0);
  const totalTrainingTime = completedRounds.reduce((sum, round) => sum + round.duration, 0);

  // Calculate weighted averages
  let totalSpeedWeighted = 0;
  let totalWpmWeighted = 0;
  let totalDurationForWeighting = 0;

  completedRounds.forEach(round => {
    if (round.duration > 0) {
      const weight = round.duration;
      totalSpeedWeighted += round.speed * weight;
      totalWpmWeighted += round.wpm * weight;
      totalDurationForWeighting += weight;
    }
  });

  const weightedSpeed = totalDurationForWeighting > 0 ? totalSpeedWeighted / totalDurationForWeighting : 0;
  const weightedWpm = totalDurationForWeighting > 0 ? totalWpmWeighted / totalDurationForWeighting : 0;

  return {
    totalLetters: correctLetters, // Using correct letters as total meaningful letters
    correctLetters,
    incorrectLetters: totalAttempts - correctLetters,
    totalAttempts,
    wrongAttempts: totalAttempts - correctLetters,
    accuracy: totalAttempts > 0 ? (correctLetters / totalAttempts) * 100 : 0,
    speed: weightedSpeed,
    duration: totalTrainingTime,
    wpm: weightedWpm
  };
};

/**
 * Formats time in milliseconds to a readable format
 */
const formatTrainingTime = (milliseconds: number): string => {
  if (milliseconds === 0) return '0m';

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${seconds}s`;
  } else if (seconds === 0) {
    return `${minutes}m`;
  } else {
    return `${minutes}m ${seconds}s`;
  }
};

/**
 * Formats number to 1 decimal place if needed, otherwise integer
 */
const formatStat = (value: number): string => {
  if (value === 0) return '0';
  if (value % 1 === 0) return value.toString();
  return value.toFixed(1);
};

export const CourseDayStats: React.FC<CourseDayStatsProps> = ({
  currentDay,
  currentRound,
  courseProgress,
  customStats,
  mode = 'course',
  className = ''
}) => {
  // Get stats based on mode
  const aggregatedStats = useMemo(() => {
    if (mode === 'custom') {
      // Custom mode: use provided custom stats or empty
      return customStats || {
        totalLetters: 0,
        correctLetters: 0,
        incorrectLetters: 0,
        totalAttempts: 0,
        wrongAttempts: 0,
        accuracy: 0,
        speed: 0,
        duration: 0,
        wpm: 0
      };
    } else {
      // Course mode: show only the current round's stats
      const dayProgress = courseProgress.find((dp: any) => dp.dayNumber === currentDay);
      const round = dayProgress?.rounds.find((r: any) => r.roundNumber === currentRound);
      const stats = round?.stats || round?.bestStats;

      if (stats) {
        return aggregateRoundStats([stats]); // Use single round stats
      }

      return aggregateRoundStats([]); // Empty stats if no round or no stats
    }
  }, [mode, customStats, courseProgress, currentDay, currentRound]);

  console.log(`📊 CourseDayStats for ${mode} mode:`, {
    mode,
    currentDay: mode === 'course' ? currentDay : 'N/A',
    currentRound: mode === 'course' ? currentRound : 'N/A',
    customStats: !!customStats,
    aggregatedStats
  });

  return (
    <div className={`course-day-stats ${className}`}>
      <div className="stats-grid">
        {/* Row 1: Total Attempts | Correct | Accuracy */}
        <div className="stat-item">
          <div className="stat-label">📝 Total Attempts</div>
          <div className="stat-value">{aggregatedStats.totalAttempts}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">✅ Correct</div>
          <div className="stat-value">{aggregatedStats.correctLetters}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">🎯 Accuracy</div>
          <div className="stat-value">{formatStat(aggregatedStats.accuracy)}%</div>
        </div>

        {/* Row 2: Total Training Time | Speed (L/s) | WPM */}
        <div className="stat-item">
          <div className="stat-label">⏱️ Training Time</div>
          <div className="stat-value">{formatTrainingTime(aggregatedStats.duration)}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">⚡ Speed (L/s)</div>
          <div className="stat-value">{formatStat(aggregatedStats.speed)}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">⌨️ WPM</div>
          <div className="stat-value">{formatStat(aggregatedStats.wpm)}</div>
        </div>
      </div>

      <style>{`
        .course-day-stats {
          background: rgba(17, 24, 39, 0.4);
          border: 1px solid rgba(75, 85, 99, 0.4);
          border-radius: 6px;
          padding: 6px;
          flex: 0 0 auto;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 4px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 3px 2px;
          background: rgba(17, 24, 39, 0.6);
          border: 1px solid rgba(75, 85, 99, 0.2);
          border-radius: 4px;
          min-height: 28px;
          justify-content: center;
        }

        .stat-label {
          font-size: 0.6rem;
          color: #9ca3af;
          font-weight: 500;
          margin-bottom: 1px;
          line-height: 1;
        }

        .stat-value {
          font-size: 0.7rem;
          color: #ffffff;
          font-weight: 600;
          line-height: 1;
        }

        /* Responsive adjustments for smaller containers */
        @media (max-width: 768px) {
          .stats-grid {
            gap: 6px;
          }

          .stat-item {
            padding: 4px 2px;
            min-height: 40px;
          }

          .stat-label {
            font-size: 0.625rem;
          }

          .stat-value {
            font-size: 0.8rem;
          }

          .stats-title {
            font-size: 0.8rem;
          }

          .stats-subtitle {
            font-size: 0.7rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseDayStats;
